import { hasProperty } from '@deltasierra/type-utilities';
import { Collapse } from '@material-ui/core';
import React from 'react';
import { TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { DSList } from '../../../mui/List';
import { DSListItem } from '../../../mui/List/ListItem';
import { DSListItemText } from '../../../mui/List/ListItemText';
import { DSPopperProps } from '../../../mui/Popper';
import { SvgLoading } from '../../Loaders/SvgLoading';
import { InputDropdown } from './InputDropdown';
import { InputDropdownListItem } from './InputDropdownListItem';
import { ForceReRenderableProps, useForceRerender } from './useForceRerender';

const StyledDSList = styled(DSList)`
    max-height: 200px;
    overflow: auto;
`;

const StyledLoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    padding-bottom: ${props => props.theme.spacing(1)}px;
`;

const StyledListItemText = styled(DSListItemText)`
    line-height: 24px;
    padding-left: ${props => props.theme.spacing(2)}px;
    padding-right: ${props => props.theme.spacing(2)}px;
`;

function isStringItems(items: any): items is string[] {
    return items.length > 0 && typeof items[0] === 'string';
}

function isBasicItems<T>(items: any): items is Array<InputDropdownListOptionObject<T>> {
    return items.length > 0 && typeof items[0] !== 'string' && !hasProperty(items[0], 'key');
}

function optionsToObjectFullyTyped<T>(
    options: InputDropdownListOptions<T>,
): Array<InputDropdownListOptionObjectWithId<T>> {
    if (isStringItems(options)) {
        return options.map(option => ({ key: option, label: option, value: option })) as Array<
            InputDropdownListOptionObjectWithId<T>
        >;
    } else if (isBasicItems(options)) {
        return options.map(option => ({ ...option, key: option.label }));
    } else {
        return options;
    }
}

export type InputDropdownListOptionObject<T> = { label: string; value: T };
export type InputDropdownListOptionObjectWithId<T> = { label: string; value: T; key: number | string };

export type InputDropdownListOptions<T> =
    | Array<InputDropdownListOptionObject<T>>
    | Array<InputDropdownListOptionObjectWithId<T>>
    | (T extends string ? T[] : never);

export type InputDropdownListOnSelectItemHandler<T> = (item: T, index: number) => void;
export type InputDropdownListOnHoverItemHandler<T> = (item: T, index: number) => void;

export type InputDropdownListProps<T = string> = ForceReRenderableProps & {
    disablePortal?: boolean;
    listStyle?: React.CSSProperties;
    loading?: boolean;
    modifiers?: DSPopperProps['modifiers'];
    noItemsMessage?: React.ReactNode;
    onClickAway?: () => void;
    onHoverItem?: InputDropdownListOnHoverItemHandler<T>;
    onNavigateDown?: () => void;
    onNavigateUp?: () => void;
    onSelectItem?: InputDropdownListOnSelectItemHandler<T>;
    open: boolean;
    options: InputDropdownListOptions<T>;
    popperAnchorEl: HTMLElement | null;
    selectedIndex?: number;
};

export function InputDropdownList<T>({
    disablePortal,
    forceReRenderProp,
    listStyle,
    loading,
    modifiers,
    noItemsMessage,
    onClickAway,
    onHoverItem,
    onNavigateDown,
    onNavigateUp,
    onSelectItem,
    open,
    options,
    popperAnchorEl,
    selectedIndex,
}: InputDropdownListProps<T>): JSX.Element {
    const handleSelectItemByIndex = React.useCallback(
        (itemIndex: number) => {
            const found = itemIndex >= 0 && itemIndex < options.length ? options[itemIndex] : null;
            if (found) {
                onSelectItem?.(typeof found === 'string' ? found : found.value, itemIndex);
            }
        },
        [onSelectItem, options],
    );

    const handleHoverItemByIndex = React.useCallback(
        (itemIndex: number) => {
            const found = itemIndex >= 0 && itemIndex < options.length ? options[itemIndex] : null;
            if (found) {
                onHoverItem?.(typeof found === 'string' ? found : found.value, itemIndex);
            }
        },
        [onHoverItem, options],
    );

    const handleKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLUListElement>>(
        event => {
            if (event.key === 'ArrowUp') {
                onNavigateUp?.();
                event.preventDefault();
            } else if (event.key === 'ArrowDown') {
                onNavigateDown?.();
                event.preventDefault();
            } else if (event.key === 'Enter') {
                if (selectedIndex !== undefined) {
                    handleSelectItemByIndex(selectedIndex);
                }
            }
        },
        [onNavigateDown, onNavigateUp, handleSelectItemByIndex, selectedIndex],
    );

    const renderedItems = React.useMemo(() => {
        const mappedForRender = optionsToObjectFullyTyped(options);

        return (
            <TransitionGroup timeout={100}>
                {mappedForRender.map((item, index) => (
                    <Collapse key={item.key}>
                        <InputDropdownListItem
                            label={item.label}
                            selected={selectedIndex === index}
                            onClick={() => {
                                handleSelectItemByIndex(index);
                            }}
                            onMouseEnter={() => {
                                handleHoverItemByIndex(index);
                            }}
                        />
                    </Collapse>
                ))}
            </TransitionGroup>
        );
    }, [options, handleHoverItemByIndex, handleSelectItemByIndex, selectedIndex]);

    return (
        <InputDropdown
            disablePortal={disablePortal}
            forceReRenderProp={forceReRenderProp}
            modifiers={modifiers}
            noPadding
            open={open}
            popperAnchorEl={popperAnchorEl}
            onClickAway={onClickAway}
        >
            <StyledDSList style={listStyle} onKeyDown={handleKeyDown}>
                {renderedItems}
                {options.length === 0 && (
                    <DSListItem>
                        <StyledListItemText>{noItemsMessage}</StyledListItemText>
                    </DSListItem>
                )}
            </StyledDSList>
            {loading && (
                <StyledLoadingWrapper>
                    <SvgLoading inline size="small" />
                </StyledLoadingWrapper>
            )}
        </InputDropdown>
    );
}
InputDropdownList.displayName = 'InputDropdown';
InputDropdownList.useForceRerender = useForceRerender;
